"use strict";

import { UIHelper } from "./ui-helper";
import {
  WebPushProvisioningAppleClient,
  WebPushProvisioningGoogleClient,
} from "./web-push-provisioning-client";
import { WebSecureDisplayClient } from "./web-secure-display-client";
import { WebSecureInputClient } from "./web-secure-input-client";

/**
 * Interface pour la configuration
 */
export class Config {
  apiUrl: string;
  xIssuerId: string;
  webDisplayXUserId: string;
  webDisplayToken: string;
  webInputXUserId: string;
  webInputToken: string;
  webPushProvisioningXUserId: string;
  webPushProvisioningToken: string;
  cssUrl: string;
  cssUrlVertical: string;
}

/**
 * WebSecureDemoController - Contrôleur pour la logique métier de la démo WebSecure
 */
export class WebSecureDemoController {
  private apiUrl: string = "";
  private xIssuerId: string = "";
  private webDisplayXUserId: string = "";
  private webDisplayToken: string = "";
  private webInputXUserId: string = "";
  private webInputToken: string = "";
  private webPushProvisioningXUserId: string = "";
  private webPushProvisioningToken: string = "";
  private cssUrl: string = "";
  private cssUrlVertical: string = "";

  constructor(config: Config) {
    this.apiUrl = config.apiUrl;
    this.xIssuerId = config.xIssuerId;
    this.webDisplayXUserId = config.webDisplayXUserId;
    this.webDisplayToken = config.webDisplayToken;
    this.webInputXUserId = config.webInputXUserId;
    this.webInputToken = config.webInputToken;
    this.webPushProvisioningXUserId = config.webPushProvisioningXUserId;
    this.webPushProvisioningToken = config.webPushProvisioningToken;
    this.cssUrl = config.cssUrl;
    this.cssUrlVertical = config.cssUrlVertical;
  }

  /**
   * Génère un code PIN aléatoire
   */
  private generateRandomPinCode(): string {
    return (
      (Math.random() + " ").substring(2, 4) +
      (Math.random() + " ").substring(2, 4)
    );
  }

  /**
   * Génère un PAN aléatoire
   */
  private generateRandomPan(): string {
    return (
      "4123" +
      (Math.random() + " ").substring(2, 10) +
      (Math.random() + " ").substring(2, 6)
    );
  }

  /**
   * Génère un code aléatoire (CVV)
   */
  private generateRandomCode(): string {
    return (Math.random() + " ").substring(2, 5);
  }

  /**
   * Génère un issuerCardId selon le type de sécurité
   */
  private generateIssuerCardId(secureType: string): string {
    const pinCodeRandom = this.generateRandomPinCode();
    const panRandom = this.generateRandomPan();
    const codeRandom = this.generateRandomCode();

    return secureType === WebSecureDisplayClient.PIN_CODE
      ? `${panRandom}ByIssCrdId${pinCodeRandom}`
      : `${panRandom}ByIssCrdId${codeRandom}`;
  }

  /**
   * Requête d'affichage WebSecure (Display ou Input)
   */
  public requestWebSecure(): void {
    UIHelper.clearContainer("message-container");

    const secureTypeElement = document.getElementById(
      "secureType",
    ) as HTMLSelectElement;
    const secureType = secureTypeElement.value;
    if (secureType.startsWith("WPP")) {
      UIHelper.clearContainer("webdisplay-container");
      UIHelper.clearContainer("webinput-container");
      if (secureType === "WPP_APPLE") {
        this.requestWPPApple();
      } else if (secureType === "WPP_GOOGLE") {
        this.requestWPPGoogle();
      }
    } else if (secureType.startsWith("INPUT")) {
      UIHelper.clearContainer("webdisplay-container");
      this.requestWebInput();
    } else {
      UIHelper.clearContainer("webinput-container");
      this.requestWebDisplay();
    }
  }

  /**
   * Requête WebSecure avec erreur
   */
  public requestWebSecureWithError(): void {
    UIHelper.clearContainer("message-container");

    const secureTypeElement = document.getElementById(
      "secureType",
    ) as HTMLSelectElement;
    const secureType = secureTypeElement.value;

    if (secureType.startsWith("INPUT")) {
      UIHelper.clearContainer("webdisplay-container");
      this.requestWebInput(true);
    } else {
      UIHelper.clearContainer("webinput-container");
      this.requestWebDisplay(true);
    }
  }

  /**
   * Initialise et affiche un WebSecureDisplay
   */
  public requestWebDisplay(withError: boolean = false): void {
    const containerId = "webdisplay-container";
    const enableCopyElement = document.getElementById(
      "enableCopy",
    ) as HTMLInputElement;
    const enableCopy = enableCopyElement?.checked ?? false;

    const verticalDisplayElement = document.getElementById(
      "verticalDisplay",
    ) as HTMLInputElement;
    const webDisplay = new WebSecureDisplayClient(
      this.apiUrl,
      this.xIssuerId,
      this.webDisplayXUserId,
      this.webDisplayToken,
      verticalDisplayElement?.checked ? this.cssUrlVertical : this.cssUrl,
      3600,
      enableCopy,
    );

    webDisplay.onDisplaySuccess = () => {
      UIHelper.displayMessage("message-container", "Success", "msg-success");
    };
    webDisplay.onDisplayError = () => {
      UIHelper.displayMessage("message-container", "Error", "msg-error");
    };

    const secureTypeElement = document.getElementById(
      "secureType",
    ) as HTMLSelectElement;
    const secureType = secureTypeElement.value;

    let ipAddress: string | null = null;
    const ipAddressElement = document.getElementById(
      "ipAddress",
    ) as HTMLInputElement;
    if (ipAddressElement && ipAddressElement.value) {
      ipAddress = ipAddressElement.value;
    }

    const issuerCardId = this.generateIssuerCardId(secureType);

    if (withError) {
      webDisplay.initializeIframeWithError(
        containerId,
        secureType,
        issuerCardId,
      );
    } else {
      webDisplay.initializeIframe(
        containerId,
        secureType,
        issuerCardId,
        ipAddress,
      );
    }
  }

  /**
   * Initialise et affiche un WebSecureInput
   */
  public requestWebInput(withError: boolean = false): void {
    const containerId = "webinput-container";
    const webInput = new WebSecureInputClient(
      this.apiUrl,
      this.xIssuerId,
      this.webInputXUserId,
      this.webInputToken,
      this.cssUrl,
      3600,
    );

    webInput.onPinReady = () => {
      UIHelper.displayMessage("message-container", "Pin Ready", "msg-success");
      UIHelper.displaySecureInputButton(containerId, webInput);
    };
    webInput.onIframeReady = () => {
      UIHelper.displayMessage(
        "message-container",
        "Iframe Ready",
        "msg-success",
      );
      UIHelper.hideSecureInputButton(containerId);
    };
    webInput.onInit = () => {
      UIHelper.displayMessage("message-container", "Init", "msg-success");
      UIHelper.hideSecureInputButton(containerId);
    };
    webInput.onError = () => {
      UIHelper.displayMessage("message-container", "Error", "msg-error");
      UIHelper.hideSecureInputButton(containerId);
    };
    webInput.onSuccess = (body: any) => {
      UIHelper.displayMessage(
        "message-container",
        "Success " + JSON.stringify(body),
        "msg-success",
      );
      UIHelper.displaySecureInputButton(containerId, webInput);
    };
    webInput.onPinNotReady = () => {
      UIHelper.displayMessage(
        "message-container",
        "Pin Not Ready",
        "msg-warning",
      );
      UIHelper.hideSecureInputButton(containerId);
    };
    webInput.onPinConfirmationError = () => {
      UIHelper.displayMessage(
        "message-container",
        "Pin Confirmation error",
        "msg-error",
      );
      UIHelper.hideSecureInputButton(containerId);
    };

    const secureTypeElement = document.getElementById(
      "secureType",
    ) as HTMLSelectElement;
    let secureType = secureTypeElement.value;
    secureType = secureType.substring(
      secureType.indexOf("_") + 1,
      secureType.length,
    );

    const withConfirmElement = document.getElementById(
      "withConfirm",
    ) as HTMLInputElement;
    const withConfirm = withConfirmElement?.checked ?? false;

    let keyboardType = "SYSTEM";
    const withKeyboardElement = document.getElementById(
      "withKeyboard",
    ) as HTMLInputElement;
    const withRandomKeyboardElement = document.getElementById(
      "withRandomKeyboard",
    ) as HTMLInputElement;

    if (withKeyboardElement?.checked) {
      keyboardType = "NORMAL";
    } else if (withRandomKeyboardElement?.checked) {
      keyboardType = "RANDOMIZED";
    }

    const inputNumberElement = document.getElementById(
      "inputNumber",
    ) as HTMLInputElement;
    const numberOfInput = parseInt(inputNumberElement.value, 10);

    const encryptionTypeElement = document.getElementById(
      "encryptionType",
    ) as HTMLSelectElement;
    const encryptionType = encryptionTypeElement.value;

    const issuerCardId = this.generateIssuerCardId(secureType);

    if (withError) {
      webInput.initializeIframeWithError(
        containerId,
        secureType,
        issuerCardId,
        numberOfInput,
        withConfirm,
        keyboardType,
        encryptionType,
      );
    } else {
      webInput.initializeIframe(
        containerId,
        secureType,
        issuerCardId,
        numberOfInput,
        withConfirm,
        keyboardType,
        encryptionType,
      );
    }
  }

  /**
   * Reads the 'wppUseMock' checkbox and sets the global flag consumed by
   * `script-loader.ts` to switch between the real Apple/Google scripts and
   * their local mocks. Must be called before instantiating the WPP client.
   */
  private applyWppMockSetting(): void {
    const checkbox = document.getElementById(
      "wppUseMock",
    ) as HTMLInputElement | null;
    (window as any).__WPP_USE_MOCK_SCRIPTS__ = checkbox?.checked ?? true;
  }

  public requestWPPApple(withError: boolean = false): void {
    this.applyWppMockSetting();
    new WebPushProvisioningAppleClient(
      this.apiUrl,
      this.xIssuerId,
      this.webPushProvisioningXUserId,
      this.webPushProvisioningToken,
    );
  }

  public requestWPPGoogle(withError: boolean = false): void {
    this.applyWppMockSetting();
    new WebPushProvisioningGoogleClient(
      this.apiUrl,
      this.xIssuerId,
      this.webPushProvisioningXUserId,
      this.webPushProvisioningToken,
    );
  }

  /**
   * Gère le changement de type de sécurité et met à jour les options de l'UI
   */
  public handleTypeChange(): void {
    const secureTypeElement = document.getElementById(
      "secureType",
    ) as HTMLSelectElement;
    const secureType = secureTypeElement.value;
    const optContainerId = "option-container";
    const parent = document.getElementById(optContainerId);

    if (parent) {
      parent.innerHTML = "";
      if (secureType.startsWith("WPP")) {
        UIHelper.createCheckBoxInput(
          parent,
          "wppUseMock",
          "Use mock script (no real Apple/Google call)",
        );
        const wppUseMock = document.getElementById(
          "wppUseMock",
        ) as HTMLInputElement | null;
        if (wppUseMock) {
          wppUseMock.checked = true;
        }
      } else if (secureType.startsWith("INPUT")) {
        UIHelper.createCheckBoxInput(parent, "withConfirm", "With Pin Confirm");
        UIHelper.createRadioButtonInput(
          parent,
          "withoutKeyBoard",
          "withKeyboard",
          "Without keyboard",
          "SYSTEM",
        );
        UIHelper.createRadioButtonInput(
          parent,
          "withKeyboard",
          "withKeyboard",
          "With normal keyboard",
          "NORMAL",
        );
        UIHelper.createRadioButtonInput(
          parent,
          "withRandomKeyboard",
          "withKeyboard",
          "With random keyboard",
          "RANDOMIZED",
        );
        UIHelper.createNumberInput(parent, "inputNumber", "Number of input", 4);
        UIHelper.createSelectInput(
          parent,
          "encryptionType",
          "Encryption Type",
          ["JWE_RSA_OAEP256_GCM256", "JWE_RSA_OAEP256_CBC256"],
        );
      } else {
        UIHelper.createCheckBoxInput(parent, "enableCopy", "Enable copy");
        UIHelper.createCheckBoxInput(parent, "enableIpCheck", "Enable Ip");
        if (secureType.startsWith("CARD")) {
          UIHelper.createCheckBoxInput(
            parent,
            "verticalDisplay",
            "Use vertical CSS",
          );
        }
        const enableIpCheckElement = document.getElementById("enableIpCheck");
        if (enableIpCheckElement) {
          enableIpCheckElement.addEventListener(
            "click",
            UIHelper.toggleIpAddressField,
          );
        }
      }
    }
  }

  /**
   * Retourne l'issuer ID
   */
  public getXIssuerId(): string {
    return this.xIssuerId;
  }
}
